'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { isFeatureEnabled } from '@/lib/features';

// Mega Menu per Servizi
function ServiziMegaMenu({ isVisible }: { isVisible: boolean }) {
  return (
    <div 
      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[700px] bg-white rounded-2xl shadow-2xl shadow-secondary-500/15 border border-secondary-100 overflow-hidden transition-all duration-300 ${
        isVisible ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-4'
      }`}
    >
      <div className="flex">
        {/* Left - Image */}
        <div className="w-56 bg-gradient-to-br from-secondary-600 to-secondary-800 p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-white font-bold text-lg mb-2">I nostri servizi</h3>
            <p className="text-secondary-400 text-sm">
              Soluzioni complete per la manifattura additiva
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-primary-400">3</span>
              <span className="text-secondary-400 text-sm">Servizi<br/>principali</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-primary-400">7+</span>
              <span className="text-secondary-400 text-sm">Tecnologie<br/>disponibili</span>
            </div>
          </div>
          {/* Placeholder image effect */}
          <div className="mt-4 h-24 bg-gradient-to-t from-secondary-900/50 to-transparent rounded-lg flex items-end justify-center pb-2">
            <svg className="w-16 h-16 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
            </svg>
          </div>
        </div>

        {/* Right - Services */}
        <div className="flex-1 p-6">
          <div className="grid grid-cols-1 gap-4">
            <Link href="/servizi/stampa-3d" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 4v16M18 4v16M6 12h12" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 transition-colors">Stampa 3D</h4>
                  <span className="px-2 py-0.5 bg-primary-100 text-primary-600 text-xs font-medium rounded-full">Popolare</span>
                </div>
                <p className="text-secondary-400 text-sm mt-1">Prototipi e produzioni con tecnologie FDM, SLA e SLS</p>
              </div>
            </Link>

            <Link href="/servizi/scansione-3d" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 transition-colors">Scansione 3D e Reverse Engineering</h4>
                <p className="text-secondary-400 text-sm mt-1">Digitalizzazione oggetti e creazione modelli editabili</p>
              </div>
            </Link>

            <Link href="/servizi/progettazione-cad" className="group flex items-start gap-4 p-4 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 transition-colors">Progettazione CAD</h4>
                <p className="text-secondary-400 text-sm mt-1">Da sketch e idee a modelli 3D pronti per la stampa</p>
              </div>
            </Link>
          </div>

          <div className="mt-4 pt-4 border-t border-secondary-100">
            <Link href="/servizi" className="inline-flex items-center gap-2 text-primary-600 font-medium text-sm hover:text-primary-700 transition-colors">
              Vedi tutti i servizi
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mega Menu per Tecnologie
function TecnologieMegaMenu({ isVisible }: { isVisible: boolean }) {
  return (
    <div 
      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white rounded-2xl shadow-2xl shadow-secondary-500/15 border border-secondary-100 overflow-hidden transition-all duration-300 ${
        isVisible ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-4'
      }`}
    >
      <div className="flex">
        {/* Left - Image */}
        <div className="w-48 bg-gradient-to-br from-primary-600 to-primary-800 p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-white font-bold mb-2">Tecnologie</h3>
            <p className="text-primary-200 text-sm">
              Stampa 3D professionale
            </p>
          </div>
          {/* Placeholder 3D printer icon */}
          <div className="mt-4 flex justify-center">
            <svg className="w-20 h-20 text-primary-300/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.75} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
            </svg>
          </div>
        </div>

        {/* Right - Technologies */}
        <div className="flex-1 p-5">
          <div className="space-y-3">
            <Link href="/tecnologie/fdm" className="group flex items-center gap-3 p-3 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-sm">
                FDM
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 text-sm">Fused Deposition Modeling</h4>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Economico</span>
                </div>
                <p className="text-secondary-400 text-xs mt-0.5">Filamento fuso, ideale per prototipi</p>
              </div>
            </Link>

            <Link href="/tecnologie/sla" className="group flex items-center gap-3 p-3 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center font-bold text-sm">
                SLA
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 text-sm">Stereolitografia</h4>
                  <span className="px-2 py-0.5 bg-violet-100 text-violet-600 text-xs font-medium rounded-full">Alta precisione</span>
                </div>
                <p className="text-secondary-400 text-xs mt-0.5">Resina fotopolimerica, dettagli fini</p>
              </div>
            </Link>

            <Link href="/tecnologie/sls" className="group flex items-center gap-3 p-3 rounded-xl hover:bg-primary-50 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-sm">
                SLS
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-secondary-500 group-hover:text-primary-500 text-sm">Sinterizzazione Laser</h4>
                  <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-medium rounded-full">Industriale</span>
                </div>
                <p className="text-secondary-400 text-xs mt-0.5">Polvere sinterizzata, parti resistenti</p>
              </div>
            </Link>
          </div>

          <div className="mt-4 pt-4 border-t border-secondary-100">
            <Link href="/tecnologie" className="inline-flex items-center gap-2 text-primary-600 font-medium text-sm hover:text-primary-700 transition-colors">
              Confronta le tecnologie
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mega Menu per Materiali
function MaterialiMegaMenu({ isVisible }: { isVisible: boolean }) {
  return (
    <div 
      className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[800px] bg-white rounded-2xl shadow-2xl shadow-secondary-500/15 border border-secondary-100 overflow-hidden transition-all duration-300 ${
        isVisible ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-4'
      }`}
    >
      <div className="flex">
        {/* Left - Image & Stats */}
        <div className="w-52 bg-gradient-to-br from-secondary-700 to-secondary-900 p-5 flex flex-col">
          <div>
            <h3 className="text-white font-bold mb-1">Materiali</h3>
            <p className="text-secondary-400 text-sm">
              Prototipi e produzione
            </p>
          </div>
          
          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-primary-400">14+</span>
              <span className="text-secondary-400 text-xs">Materiali<br/>disponibili</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-primary-400">3</span>
              <span className="text-secondary-400 text-xs">Tecnologie<br/>di stampa</span>
            </div>
          </div>

          {/* Placeholder image */}
          <div className="mt-auto pt-4">
            <div className="h-28 bg-secondary-800 rounded-lg flex items-center justify-center">
              <svg className="w-16 h-16 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Right - Materials by technology */}
        <div className="flex-1 p-5">
          <div className="grid grid-cols-3 gap-6">
            {/* FDM Column */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-secondary-100">
                <div className="w-6 h-6 rounded bg-primary-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">F</span>
                </div>
                <span className="font-semibold text-secondary-500 text-sm">FDM</span>
              </div>
              <div className="space-y-1">
                <Link href="/materiali/pla" className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  PLA
                  <span className="px-1.5 py-0.5 bg-primary-100 text-primary-600 text-[10px] font-medium rounded">Popolare</span>
                </Link>
                <Link href="/materiali/petg" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  PETG
                </Link>
                <Link href="/materiali/abs" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  ABS
                </Link>
                <Link href="/materiali/pc" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  PC
                </Link>
                <Link href="/materiali/nylon-12" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Nylon 12
                </Link>
                <Link href="/materiali/tpu-fdm" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  TPU
                </Link>
                <Link href="/materiali/onyx" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Onyx
                </Link>
              </div>
            </div>

            {/* SLA Column */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-secondary-100">
                <div className="w-6 h-6 rounded bg-violet-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">S</span>
                </div>
                <span className="font-semibold text-secondary-500 text-sm">SLA</span>
              </div>
              <div className="space-y-1">
                <Link href="/materiali/resina-standard" className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Resina Standard
                  <span className="px-1.5 py-0.5 bg-violet-100 text-violet-600 text-[10px] font-medium rounded">Popolare</span>
                </Link>
                <Link href="/materiali/resina-abs-like" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Resina ABS-like
                </Link>
                <Link href="/materiali/resina-tough" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Resina Tough
                </Link>
              </div>
            </div>

            {/* SLS Column */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-secondary-100">
                <div className="w-6 h-6 rounded bg-rose-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">S</span>
                </div>
                <span className="font-semibold text-secondary-500 text-sm">SLS</span>
              </div>
              <div className="space-y-1">
                <Link href="/materiali/nylon-pa12" className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Nylon PA12
                  <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-medium rounded">Popolare</span>
                </Link>
                <Link href="/materiali/nylon-pa11" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  Nylon PA11
                </Link>
                <Link href="/materiali/tpu" className="block py-1.5 px-2 rounded-lg hover:bg-primary-50 text-secondary-500 hover:text-primary-500 text-sm transition-colors">
                  TPU 95A
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-secondary-100 flex items-center justify-between">
            <Link href="/materiali" className="inline-flex items-center gap-2 text-primary-600 font-medium text-sm hover:text-primary-700 transition-colors">
              Vedi tutti i materiali
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <span className="text-secondary-400 text-xs">Consegna in 3-5 giorni</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple dropdown for Blog, Contatti etc.
function SimpleDropdown({ items, isVisible }: { items: { href: string; label: string }[]; isVisible: boolean }) {
  return (
    <div 
      className={`absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl shadow-secondary-500/10 border border-secondary-100 py-2 transition-all duration-200 ${
        isVisible ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
      }`}
    >
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="block px-4 py-2.5 text-secondary-500 hover:bg-primary-50 hover:text-primary-500 transition-colors text-sm"
        >
          {item.label}
        </Link>
      ))}
    </div>
  );
}

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/servizi', label: 'Servizi', megaMenu: 'servizi' },
  { href: '/tecnologie', label: 'Tecnologie', megaMenu: 'tecnologie' },
  { href: '/materiali', label: 'Materiali', megaMenu: 'materiali' },
  { href: '/blog', label: 'Blog' },
  { href: '/contatti', label: 'Contatti' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const renderMegaMenu = (menuType: string, isVisible: boolean) => {
    switch (menuType) {
      case 'servizi':
        return <ServiziMegaMenu isVisible={isVisible} />;
      case 'tecnologie':
        return <TecnologieMegaMenu isVisible={isVisible} />;
      case 'materiali':
        return <MaterialiMegaMenu isVisible={isVisible} />;
      default:
        return null;
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled
          ? 'shadow-lg shadow-secondary-500/5'
          : 'shadow-md shadow-secondary-500/5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="group">
            {/* eslint-disable-next-line @next/next/no-img-element -- logo pre-ottimizzato 1x/2x */}
            <img
              src="/images/tds-logo-162.png"
              srcSet="/images/tds-logo-162.png 1x, /images/tds-logo-324.png 2x"
              alt="TDS 3D Printing"
              width={162}
              height={56}
              className="group-hover:scale-105 transition-transform h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <div
                key={link.href}
                className="relative"
                onMouseEnter={() => link.megaMenu && setOpenDropdown(link.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  href={link.href}
                  className="flex items-center gap-1 text-secondary-400 hover:text-primary-500 font-medium transition-colors py-2"
                >
                  {link.label}
                  {link.megaMenu && (
                    <svg 
                      className={`w-4 h-4 transition-transform ${openDropdown === link.label ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </Link>
                {link.megaMenu && renderMegaMenu(link.megaMenu, openDropdown === link.label)}
              </div>
            ))}
          </div>

          {/* CTA + Account */}
          <div className="hidden lg:flex items-center gap-3">
            {isFeatureEnabled('account') && (
              <Link
                href="/account"
                className="flex items-center gap-2 px-4 py-2.5 text-secondary-400 hover:text-primary-500 font-medium transition-colors rounded-lg hover:bg-primary-50"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account
              </Link>
            )}
            <Link 
                href="/contatti"
                className="btn-primary"
              >
                Preventivo
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-secondary-500"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-secondary-100 bg-white">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <div key={link.href}>
                  {link.megaMenu ? (
                    <>
                      <button
                        onClick={() => setMobileOpenDropdown(mobileOpenDropdown === link.label ? null : link.label)}
                        className="flex items-center justify-between w-full text-secondary-400 hover:text-primary-500 font-medium py-3 px-2"
                      >
                        {link.label}
                        <svg 
                          className={`w-4 h-4 transition-transform ${mobileOpenDropdown === link.label ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {mobileOpenDropdown === link.label && (
                        <div className="pl-4 pb-2 space-y-1">
                          {link.megaMenu === 'servizi' && (
                            <>
                              <Link href="/servizi/stampa-3d" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Stampa 3D</Link>
                              <Link href="/servizi/scansione-3d" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Scansione 3D</Link>
                              <Link href="/servizi/progettazione-cad" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Progettazione CAD</Link>
                            </>
                          )}
                          {link.megaMenu === 'tecnologie' && (
                            <>
                              <Link href="/tecnologie/fdm" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>FDM</Link>
                              <Link href="/tecnologie/sla" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>SLA</Link>
                              <Link href="/tecnologie/sls" className="block py-2 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>SLS</Link>
                            </>
                          )}
                          {link.megaMenu === 'materiali' && (
                            <>
                              <p className="text-xs text-secondary-400 uppercase tracking-wide px-2 pt-2">FDM</p>
                              <Link href="/materiali/pla" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>PLA</Link>
                              <Link href="/materiali/petg" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>PETG</Link>
                              <Link href="/materiali/abs" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>ABS</Link>
                              <Link href="/materiali/pc" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>PC</Link>
                              <Link href="/materiali/nylon-12" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Nylon 12</Link>
                              <Link href="/materiali/tpu-fdm" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>TPU</Link>
                              <Link href="/materiali/onyx" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Onyx</Link>
                              <p className="text-xs text-secondary-400 uppercase tracking-wide px-2 pt-2">SLA</p>
                              <Link href="/materiali/resina-standard" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Resina Standard</Link>
                              <Link href="/materiali/resina-abs-like" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Resina ABS-like</Link>
                              <Link href="/materiali/resina-tough" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Resina Tough</Link>
                              <p className="text-xs text-secondary-400 uppercase tracking-wide px-2 pt-2">SLS</p>
                              <Link href="/materiali/nylon-pa12" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Nylon PA12</Link>
                              <Link href="/materiali/nylon-pa11" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>Nylon PA11</Link>
                              <Link href="/materiali/tpu" className="block py-1.5 px-2 text-secondary-400 hover:text-primary-500 text-sm" onClick={() => setIsMobileMenuOpen(false)}>TPU 95A</Link>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      href={link.href}
                      className="block text-secondary-400 hover:text-primary-500 font-medium py-3 px-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              {isFeatureEnabled('account') && (
                <Link
                  href="/account"
                  className="flex items-center gap-2 text-secondary-400 hover:text-primary-500 font-medium py-3 px-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Account
                </Link>
              )}
              <Link
                  href="/contatti"
                  className="btn-primary mt-4 mx-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Preventivo
                </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
